import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useConditions } from "@/hooks/useMeta";

const num = (v) => (v === "" ? null : Number(v));

/** Structured condition inputs; the server computes the only valid class and flags contradictions. */
export default function ConditionFields({ category, value, onChange, onCheck }) {
  const { data: schemes } = useConditions();
  const schemeKey = category?.condition_scheme;
  const scheme = schemeKey ? schemes?.[schemeKey] : null;
  const [check, setCheck] = useState(null);
  const set = (patch) => onChange({ ...value, ...patch });

  useEffect(() => {
    if (!category) return;
    const t = setTimeout(async () => {
      const { data } = await api.post("/listings/condition-check", { category: category.slug, condition: value });
      setCheck(data);
      onCheck?.(data);
    }, 250);
    return () => clearTimeout(t);
  }, [JSON.stringify(value), category?.slug]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4 panel p-5" data-testid="condition-fields">
      <p className="font-display font-semibold">Ürün kondisyonu <span className="text-xs font-normal text-zinc-500">(satıcı beyanı)</span></p>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label>Çalışma durumu</Label>
          <Select value={value.functional_status || "ok"} onValueChange={(v) => set({ functional_status: v })}>
            <SelectTrigger data-testid="condition-functional-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="ok">Sorunsuz</SelectItem><SelectItem value="issue">Sorunlu</SelectItem><SelectItem value="broken">Arızalı</SelectItem></SelectContent>
          </Select>
        </div>
        {schemeKey === "component" && (
          <>
            <div className="space-y-1.5"><Label>Kullanım süresi (gün)</Label><Input data-testid="condition-days-input" type="number" min="0" value={value.usage_days ?? ""} onChange={(e) => set({ usage_days: num(e.target.value) })} /></div>
            <div className="space-y-1.5"><Label>Kaza sayısı</Label><Input data-testid="condition-crash-input" type="number" min="0" value={value.crash_count ?? ""} onChange={(e) => set({ crash_count: num(e.target.value) })} /></div>
          </>
        )}
        {schemeKey === "radio_goggles" && (
          <div className="space-y-1.5"><Label>Kullanım süresi (ay)</Label><Input data-testid="condition-months-input" type="number" min="0" step="0.5" value={value.usage_months ?? ""} onChange={(e) => set({ usage_months: num(e.target.value) })} /></div>
        )}
      </div>
      {scheme && (
        <div className="space-y-1.5">
          <Label>Kondisyon sınıfı</Label>
          <Select value={value.class_code || ""} onValueChange={(v) => set({ class_code: v })}>
            <SelectTrigger data-testid="condition-class-select"><SelectValue placeholder="Sınıf seçin" /></SelectTrigger>
            <SelectContent>{scheme.classes.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} — {c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      {check?.expected_class && !check.warning && (
        <p className="flex items-center gap-2 text-sm text-emerald-700" data-testid="condition-ok"><CheckCircle2 className="h-4 w-4" />Bilgilerinize göre sınıf: <b>{check.expected_class}</b></p>
      )}
      {check?.warning && <p className="flex gap-2 text-sm text-red-700" data-testid="condition-warning"><AlertTriangle className="h-4 w-4 shrink-0" />{check.warning}</p>}
      {check?.missing && scheme && <p className="text-xs text-zinc-500" data-testid="condition-missing">{check.missing}</p>}
      <Textarea data-testid="condition-notes-input" placeholder="Kondisyonla ilgili notlar (çizik, değişen parça vb.)" value={value.notes || ""} onChange={(e) => set({ notes: e.target.value })} />
    </div>
  );
}
